import {
  ActionIcon,
  Button,
  Checkbox,
  Divider,
  Drawer,
  Input,
  InputWrapper,
  Modal,
  Pagination,
  ScrollArea,
  Select,
  Tabs,
  Tooltip,
  createTheme,
  defaultVariantColorsResolver,
  type VariantColorsResolver,
} from "@mantine/core";

import "./fonts";
import classes from "./theme.module.css";
import { indigo, shadows } from "./tokens";

const ui = "var(--cai-font-ui)";
const field = "var(--cai-font-field)";

/**
 * Filled and outline in the primary color take the tokens, which differ by scheme (the live app's
 * filled button is indigo 5 in light and indigo 7 in dark). Every other color keeps Mantine's.
 * The live app's own variants (text, text-hover, text-white, outlined-light, filled-light) are
 * all CSS, in theme.module.css.
 */
const variantColorResolver: VariantColorsResolver = (input) => {
  const primary = input.color === undefined || input.color === input.theme.primaryColor;
  if (primary && input.variant === "filled") {
    return {
      background: "var(--cai-color-primary)",
      hover: "var(--cai-color-primary-hover)",
      color: "var(--cai-color-on-primary)",
      border: "1px solid transparent",
    };
  }
  if (primary && input.variant === "outline") {
    return {
      background: "transparent",
      hover: "var(--cai-color-primary-soft)",
      color: "var(--cai-color-text-primary)",
      border: "1px solid var(--cai-color-border-primary)",
    };
  }
  return defaultVariantColorsResolver(input);
};

/**
 * The one Mantine theme (docs/specs/web/design-system.md, section 1): the live Created.ai look.
 * Change a look here, in tokens.ts or in theme.module.css, never per component.
 */
export const theme = createTheme({
  colors: { indigo: [...indigo] },
  primaryColor: "indigo",
  primaryShade: 5,
  fontFamily: ui,
  fontFamilyMonospace: "ui-monospace, SFMono-Regular, Menlo, Consolas, monospace",
  headings: { fontFamily: ui },
  // The reference's sm is a calc twin of xs (2px); ours is 4px (spec 1.4).
  radius: { xs: "2px", sm: "4px", md: "6px", lg: "8px", xl: "12px" },
  defaultRadius: "lg",
  shadows,
  breakpoints: { xs: "30em", sm: "48em", md: "64em", lg: "74em", xl: "90em" },
  cursorType: "pointer",
  variantColorResolver,
  components: {
    // Mantine sets a button's colors from the resolver only when it has a variant or a color, so
    // `filled` is named as the default: a plain <Button> takes the primary tokens too.
    Button: Button.extend({
      classNames: { root: classes.button },
      defaultProps: { variant: "filled" },
    }),
    ActionIcon: ActionIcon.extend({
      classNames: { root: classes.button },
      defaultProps: { variant: "filled" },
    }),
    InputWrapper: InputWrapper.extend({
      classNames: { label: classes.label, required: classes.required },
    }),
    Input: Input.extend({
      classNames: { wrapper: classes.inputWrapper, section: classes.section },
    }),
    // A select's text and options are in Inter, as the live app's are; other fields are not.
    Select: Select.extend({
      styles: { input: { fontFamily: field }, option: { fontFamily: field } },
    }),
    Checkbox: Checkbox.extend({ classNames: { input: classes.checkbox, icon: classes.check } }),
    Tabs: Tabs.extend({ classNames: { tab: classes.tab } }),
    Tooltip: Tooltip.extend({ classNames: { tooltip: classes.tooltip } }),
    Pagination: Pagination.extend({ classNames: { control: classes.pageControl } }),
    // Every dialog's × is named "Close" for a screen reader (#673), unless it names its own.
    Drawer: Drawer.extend({
      classNames: { content: classes.drawer, title: classes.drawerTitle },
      defaultProps: { closeButtonProps: { "aria-label": "Close" } },
    }),
    Modal: Modal.extend({ defaultProps: { zIndex: 201, closeButtonProps: { "aria-label": "Close" } } }),
    Divider: Divider.extend({ defaultProps: { color: "var(--cai-color-border-subtle)" } }),
    ScrollArea: ScrollArea.extend({ defaultProps: { scrollbarSize: 6 } }),
  },
});
